import { AppService } from '@services/app/app.service';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import logger from '@core/utils/logger';
import { Router } from '@angular/router';
import { PositionService } from '@services/position/position.service';
import { WizardService } from '@services/wizard/wizard.service';
import { ApiService } from '@core/modules/api/services/api/api.service';
import { LogLevel, LogRequestDTO } from '@core/modules/api/models';
import { AnalyticsService } from '@services/analytics/analytics.service';
import {
  BaseLog,
  ErrorLog,
  HttpReqLog,
  InfoLog,
  LogData,
  WarningLog
} from '@models/log';

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private data: LogData[] = [];

  constructor(
    private appService: AppService,
    private positionService: PositionService,
    private wizardService: WizardService,
    private analyticsService: AnalyticsService,
    private apiService: ApiService,
    private route: Router
  ) {}

  addLogData(data: LogData[], options: { timeKey?: string } = {}): void {
    if (!(data instanceof Array)) {
      return;
    }

    data
      .filter(x => this.isValidData(x))
      .forEach(x => this.data.push({ ...x }));

    if (options.timeKey) {
      this.data.push({ key: options.timeKey, value: new Date().getTime() });
    }
  }

  logError(error: any) {
    try {
      const errorLog = this.createErrorLog(error);

      this.sendLog('ERROR', errorLog);

      logger.error(error);

      this.analyticsService.pushErrorEvent({
        errorType: errorLog.type === 'http' ? 'backend' : 'generic',
        errorCode: errorLog.code || null,
        errorMessage: errorLog.message,
        errorField: null,
        isFatal: false
      });
    } catch (_) {}
  }

  logWarning(message: any) {
    this.sendLog('WARN', this.createWarningLog(message));
  }

  logInfo(message: any) {
    this.sendLog('INFO', this.createInfoLog(message));
  }

  logHttpReq(message: HttpRequest<any>) {
    this.sendLog('INFO', this.createHttpReqLog(message));
  }

  private sendLog(level: LogLevel, log: ErrorLog): void {
    if (this.appService.getConfig().generic.sendLogs) {
      this.apiService.putLog({ body: this.convertLog(level, log) }).subscribe();
    }
  }

  private convertLog(level: LogLevel, log: BaseLog): LogRequestDTO {
    return {
      level,
      entityId: log.positionId,
      msg: this.toMessage(log)
    };
  }

  private createInfoLog(message: any): InfoLog {
    return {
      ...this.createBaseLog(),
      message: this.toMessage(message)
    };
  }

  private createWarningLog(warning: any): WarningLog {
    return {
      ...this.createBaseLog(),
      warning: this.toMessage(warning)
    };
  }

  private createHttpReqLog(req: HttpRequest<any>): HttpReqLog {
    return {
      ...this.createBaseLog(),
      url: req.urlWithParams,
      responseType: req.responseType,
      method: req.method,
      body: req.body,
      headers: req.headers
        .keys()
        .filter(key => this.isAllowedLogHeader(key))
        .map(key => ({ key, value: req.headers.get(key) }))
    };
  }

  private createErrorLog(error: any): ErrorLog {
    if (error instanceof Error) {
      return this.createErrorLogFromError(error);
    } else if (error instanceof HttpErrorResponse) {
      return this.createErrorLogFromHttpError(error);
    } else if (typeof error === 'string') {
      return this.createErrorLogFromString(error);
    } else {
      return this.createErrorLogFromUnhandled();
    }
  }

  private createErrorLogFromError(error: Error): ErrorLog {
    return {
      ...this.createBaseLog(),
      type: 'js',
      message: error.message,
      stack: error.stack,
      code: error.name
    };
  }

  private createErrorLogFromString(error: string): ErrorLog {
    return {
      ...this.createBaseLog(),
      type: 'js',
      message: error
    };
  }

  private createErrorLogFromHttpError(error: HttpErrorResponse): ErrorLog {
    const message =
      error.error && error.error.messages && error.error.messages[0];

    return {
      ...this.createBaseLog(),
      type: 'http',
      message: (message && message.message) || error.message,
      uuid: (message && message.uuid) || '',
      corrId: error.headers.get('qinetic-correlation-id'),
      status: error.status,
      url: error.url,
      code: error.name
    };
  }

  private createErrorLogFromUnhandled(): ErrorLog {
    return { ...this.createBaseLog(), type: 'js', message: 'Unhandled error' };
  }

  private createBaseLog(): BaseLog {
    const activeStep = this.wizardService.getActiveStep();

    return {
      appVersion: {
        container: this.appService.getAppVersion('container'),
        core: this.appService.getAppVersion('core')
      },
      userAgent: navigator.userAgent,
      time: new Date().getTime(),
      page: this.route.url,
      positionId: this.positionService.getPositionId(),
      wizardCode: this.wizardService.getWizardCode(),
      stepCode: activeStep && activeStep.stepCode,
      data: this.useData()
    };
  }

  private useData(): LogData[] {
    const data = [...this.data];

    this.data = [];

    return data;
  }

  private toMessage(obj: any): string {
    let msg = '';

    if (typeof obj === 'object' && obj !== null) {
      try {
        msg = JSON.stringify(obj);
      } catch (_) {}
    } else if (typeof obj === 'string') {
      msg = obj;
    }

    return msg;
  }

  private isValidData(data: LogData): boolean {
    return (
      !!data &&
      typeof data.key === 'string' &&
      (typeof data.value == 'string' ||
        typeof data.value == 'number' ||
        typeof data.value == 'boolean')
    );
  }

  private isAllowedLogHeader(key: string): boolean {
    const authHeaderName = this.appService.getConfig().security.authHeaderName;

    return (
      !authHeaderName || authHeaderName.toLowerCase() !== key.toLowerCase()
    );
  }
}
